/**
 * Compact-number formatter for the Tokens-tab balance label.
 *
 * Mirrors the spec from docs/MONETIZATION_PIVOT.md "Number formatting":
 *     0 –   999  → exact digit count ("0", "240", "999")
 *  1 000 +       → integer K, floor ("1K", "2K", "9K", "12K", "100K")
 *
 * The decimal "1.5K" tier from the original spec was dropped 2026-05-23
 * on visual review — the pill is a chip-style badge and a "1.5K" label
 * fights the badge geometry. Floor-to-thousands keeps the pill graphical.
 *
 * Output is locale-independent — the produced string must match the
 * Apple Swift and Android Kotlin siblings byte-for-byte. Integer math
 * only, no `Intl.NumberFormat`, so a `de-DE` locale doesn't suddenly
 * render "1,5K".
 *
 * Renders the SIGNED value the server sends (show-negative, 2026-06-01): the
 * server zeroes a grace overdraft, so any negative reaching the client is a
 * punitive clawback debt that must read honestly as "-1200". The red capsule
 * (`account_attention.rows.tokens === 'alert'`) supplies the "you owe" signal;
 * this just supplies an exact, un-clamped number. A debt is shown exactly (no
 * K-compaction — debts aren't large and compaction would understate them).
 */
export function formatTokenBalance(balance: number): string {
  const value = Math.trunc(balance);

  if (value < 0) return String(value);

  // Floor (not round) so the displayed magnitude never overstates the
  // user's holdings: 1999 → "1K", 2000 → "2K". Same rule at every decade.
  return value < 1_000 ? String(value) : `${Math.floor(value / 1_000)}K`;
}
